import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import CardFilterDTO from '@src/adapters/dto/card/CardFilterDTO';
import ProcedureName from '@src/components/procedure/ProcedureName';
import ConsultantName from '@src/components/user/ConsultantName';
import { useMenu } from '@src/providers/MenuProvider';
import { useFormContext } from 'react-hook-form';
import ProcedureSpecialityName from '@src/components/procedure/ProcedureSpecialityName';
import CardChangeRequestDTO from '@src/adapters/dto/card/CardChangeRequestDTO';
import { formatDate } from '@src/utils/dateUtils';
import UserName from '@src/components/user/UserName';

interface Props {
  cards: CardChangeRequestDTO[];
  selectedCard: CardChangeRequestDTO | undefined;
  filter: CardFilterDTO;
  recordCount: number;
  onCardSelected(card: CardChangeRequestDTO): void;
  onSort(key: string): void;
}

function SearchResults({
  cards,
  filter,
  recordCount,
  onCardSelected,
  selectedCard,
  onSort,
}: Props) {
  const { menuWidth } = useMenu();
  const { setValue } = useFormContext();
  return (
    <Paper sx={{ width: `calc(100vw - ${menuWidth}px - 30px)` }}>
      <TableContainer>
        <Table
          aria-labelledby="tableConsultant"
          aria-label="Consultants preferences table"
          sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell component="th" align="left">
                <TableSortLabel
                  active={filter.sortKey === 'procedure'}
                  direction={
                    filter.sortKey === 'procedure' && !filter.sortAscending
                      ? 'desc'
                      : 'asc'
                  }
                  onClick={() => onSort('procedure')}>
                  Procedure
                </TableSortLabel>
              </TableCell>
              <TableCell component="th" align="left">
                <TableSortLabel
                  active={filter.sortKey === 'consultant'}
                  direction={
                    filter.sortKey === 'consultant' && !filter.sortAscending
                      ? 'desc'
                      : 'asc'
                  }
                  onClick={() => onSort('consultant')}>
                  Consultant
                </TableSortLabel>
              </TableCell>
              <TableCell component="th" align="left">
                <TableSortLabel
                  active={filter.sortKey === 'speciality'}
                  direction={
                    filter.sortKey === 'speciality' && !filter.sortAscending
                      ? 'desc'
                      : 'asc'
                  }
                  onClick={() => onSort('speciality')}>
                  Speciality
                </TableSortLabel>
              </TableCell>
              <TableCell component="th" align="left">
                Status
              </TableCell>
              <TableCell component="th" align="left">
                Version
              </TableCell>
              <TableCell component="th" align="left">
                Last Updated Date
              </TableCell>
              <TableCell component="th" align="left">
                Last Updated By
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cards &&
              cards.map((card, index) => {
                return (
                  <TableRow
                    hover
                    onClick={() => onCardSelected(card)}
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    sx={{
                      cursor: 'pointer',
                      ...(selectedCard &&
                        selectedCard.id === card.id && {
                          backgroundColor: 'rgba(70, 110, 229, 0.05)',
                        }),
                    }}>
                    <TableCell scope="row">
                      <ConsultantName id={card.consultantId} />
                    </TableCell>
                    <TableCell scope="row">
                      <ProcedureName id={card.procedureId} />
                    </TableCell>
                    <TableCell scope="row">
                      <ProcedureSpecialityName procedureId={card.procedureId} />
                    </TableCell>
                    <TableCell scope="row">{card.status}</TableCell>
                    <TableCell scope="row">{card.version}</TableCell>
                    <TableCell scope="row">
                      {formatDate(card.updatedAt, 'DD/MM/YYYY HH:mm:ss')}
                    </TableCell>
                    <TableCell scope="row">
                      {card.modifications.length > 0 ? (
                        <UserName
                          id={
                            card.modifications[card.modifications.length - 1]
                              .createdById
                          }
                          firstNameFirst={true}
                        />
                      ) : (
                        ''
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={filter.pageSize}
          page={filter.currentPage}
          onPageChange={(event, number) => setValue('currentPage', number)}
          onRowsPerPageChange={(event) => {
            setValue('pageSize', parseInt(event.target.value, 10));
            setValue('currentPage', 0);
          }}
        />
      </TableContainer>
    </Paper>
  );
}

export default SearchResults;
